import { Link, useLocation } from 'react-router-dom';
import { Ripple } from 'primereact/ripple';
import { classNames } from 'primereact/utils';
import { useContext, useEffect, useRef } from 'react';
import { LayoutContext } from './context/layoutcontext';
import { MenuContext } from './context/menucontext';
import { useMenuCategory } from '../contexts/MenuCategoryContext';
import { useSubmenuOverlayPosition } from './hooks/useSubmenuOverlayPosition';
import { useGetMyFavoritesQuery, useAddFavoriteMutation, useDeleteFavoriteMutation } from '../api';
import { useLanguage } from '../contexts/LanguageContext';
import { toastService } from '../utils/toastService';

const AppMenuitem = (props) => {
    const { activeMenu, setActiveMenu } = useContext(MenuContext);
    const { isSlim, isSlimPlus, isHorizontal, isDesktop, setLayoutState, layoutState, layoutConfig } = useContext(LayoutContext);
    const { selectedCategory } = useMenuCategory();
    const location = useLocation();
    const pathname = location.pathname;
    const searchParams = location.search;
    const submenuRef = useRef(null);
    const menuitemRef = useRef(null);
    const item = props.item;
    const key = props.parentKey ? props.parentKey + '-' + props.index : String(props.index);
    const isActiveRoute = item.to && pathname === item.to;
    const active = activeMenu === key || !!(activeMenu && activeMenu.startsWith(key + '-'));

    const { th } = useLanguage();
    const isFavoritable = !!(item?.to && !item?.items && item?.menuKey);
    const { favorite } = useGetMyFavoritesQuery(undefined, {
        skip: !isFavoritable,
        selectFromResult: ({ data }) => ({
            favorite: data?.find((f) => f.menuKey === item.menuKey)
        })
    });
    const [addFavorite] = useAddFavoriteMutation();
    const [deleteFavorite] = useDeleteFavoriteMutation();

    const toggleFavorite = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        try {
            if (favorite) {
                await deleteFavorite(favorite.id).unwrap();
                toastService.success(th('favorites.removed', { fallback: 'Favorilerden kaldırıldı' }), th('common.success', { fallback: 'Başarılı' }));
            } else {
                await addFavorite({
                    menuKey: item.menuKey,
                    title: item.title,
                    translationKey: item.translationKey,
                    link: item.to,
                    icon: item.icon
                }).unwrap();
                toastService.success(th('favorites.added', { fallback: 'Favorilere eklendi' }), th('common.success', { fallback: 'Başarılı' }));
            }
        } catch (e) {
            // Hatalar base.js tarafından global toast ile gösteriliyor
        }
    };

    useSubmenuOverlayPosition({
        target: menuitemRef.current,
        overlay: submenuRef.current,
        container: menuitemRef.current && menuitemRef.current.closest('.layout-menu-container'),
        when: props.root && active && (isSlim() || isSlimPlus() || isHorizontal()) && isDesktop()
    });

    useEffect(() => {
        if (layoutState.resetMenu) {
            setActiveMenu('');
            setLayoutState((prevLayoutState) => ({
                ...prevLayoutState,
                resetMenu: false
            }));
        }
    }, [layoutState.resetMenu]);

    // Kategori değiştiğinde ve item'ın children'ı varsa menüyü aç
    useEffect(() => {
        if (selectedCategory && item?.items && item.items.length > 0) {
            setActiveMenu(key);
        }
    }, [selectedCategory]);

    useEffect(() => {
        if (!(isSlim() || isSlimPlus() || isHorizontal()) && isActiveRoute) {
            setActiveMenu(key);
        }
        const url = pathname + searchParams;
        const onRouteChange = () => {
            if (!(isSlim() || isHorizontal()) && item.to && item.to === url) {
                setActiveMenu(key);
            }
        };
        onRouteChange();
    }, [pathname, searchParams, layoutConfig]);

    const itemClick = (event) => {
        //avoid processing disabled items
        if (item.disabled) {
            event.preventDefault();
            return;
        }

        // navigate with hover
        if (props.root && (isSlim() || isHorizontal() || isSlimPlus())) {
            const isSubmenu = event.currentTarget.closest('.layout-root-menuitem.active-menuitem > ul') !== null;
            if (isSubmenu)
                setLayoutState((prevLayoutState) => ({
                    ...prevLayoutState,
                    menuHoverActive: true
                }));
            else
                setLayoutState((prevLayoutState) => ({
                    ...prevLayoutState,
                    menuHoverActive: !prevLayoutState.menuHoverActive
                }));
        }

        //execute command
        if (item?.command) {
            item.command({ originalEvent: event, item: item });
        }

        // toggle active state
        if (item?.items) {
            setActiveMenu(active ? props.parentKey : key);

            if (props.root && !active && (isSlim() || isHorizontal() || isSlimPlus())) {
                setLayoutState((prevLayoutState) => ({
                    ...prevLayoutState,
                    overlaySubmenuActive: true
                }));
            }
        } else {
            if (!isDesktop()) {
                setLayoutState((prevLayoutState) => ({
                    ...prevLayoutState,
                    staticMenuMobileActive: !prevLayoutState.staticMenuMobileActive
                }));
            }

            if (isSlim() || isSlimPlus() || isHorizontal()) {
                setLayoutState((prevLayoutState) => ({
                    ...prevLayoutState,
                    menuHoverActive: false
                }));
            }

            setActiveMenu(key);
        }
    };

    const onMouseEnter = () => {
        // activate item on hover
        if (props.root && (isSlim() || isHorizontal() || isSlimPlus()) && isDesktop()) {
            if (!active && layoutState.menuHoverActive) {
                setActiveMenu(key);
            }
        }
    };

    const subMenu =
        item?.items && item?.visible !== false ? (
            <ul ref={submenuRef} className={classNames({ 'layout-root-submenulist': props.root })}>
                {item.items.map((child, i) => {
                    return <AppMenuitem item={child} index={i} className={child.badgeClass} parentKey={key} key={child.label} />;
                })}
            </ul>
        ) : null;

    return (
        <li
            ref={menuitemRef}
            className={classNames({
                'layout-root-menuitem': props.root,
                'active-menuitem': active
            })}
        >
            {props.root && item?.visible !== false && (
                <div className="layout-menuitem-root-text">
                    <span>{item.label}</span>
                    <i className="layout-menuitem-root-icon"></i>
                </div>
            )}
            {(!item?.to || item?.items) && item?.visible !== false ? (
                <>
                    <a
                        href={item?.url}
                        onClick={(e) => itemClick(e)}
                        className={classNames(item?.class, 'p-ripple tooltip-target', {
                            'has-submenu': item?.items && item.items.length > 0
                        })}
                        target={item?.target}
                        data-pr-tooltip={item?.label}
                        data-pr-disabled={!(isSlim() && props.root && !layoutState.menuHoverActive)}
                        tabIndex={0}
                        onMouseEnter={onMouseEnter}
                    >
                        <i className={classNames('layout-menuitem-icon', item?.icon)}></i>
                        <span className="layout-menuitem-text">{item?.label}</span>
                        {item?.items && <i className="pi pi-fw pi-angle-down layout-submenu-toggler"></i>}
                        <Ripple />
                    </a>
                </>
            ) : null}

            {item?.to && !item?.items && item?.visible !== false ? (
                <>
                    <Link
                        to={item.to}
                        replace={item?.replaceUrl}
                        onClick={(e) => itemClick(e)}
                        className={classNames(item?.class, 'p-ripple ', {
                            'active-route': isActiveRoute
                        })}
                        tabIndex={0}
                        onMouseEnter={onMouseEnter}
                    >
                        <i className={classNames('layout-menuitem-icon', item?.icon)}></i>
                        <span className="layout-menuitem-text">{item?.label}</span>
                        {isFavoritable && (
                            <i
                                role="button"
                                tabIndex={0}
                                onClick={toggleFavorite}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') toggleFavorite(e);
                                }}
                                className={classNames('layout-menuitem-favorite-toggle ml-auto', favorite ? 'pi pi-star-fill' : 'pi pi-star')}
                                title={favorite ? th('favorites.remove', { fallback: 'Favorilerden kaldır' }) : th('favorites.add', { fallback: 'Favorilere ekle' })}
                            ></i>
                        )}
                        {item?.items && <i className="pi pi-fw pi-angle-down layout-submenu-toggler"></i>}
                        <Ripple />
                    </Link>
                </>
            ) : null}
            {subMenu}
        </li>
    );
};

export default AppMenuitem;
